import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from 'lucide-react';
type ReviewForm = {
  name: string;
  location: string;
  rating: number;
  comment: string;
};
type ReviewFormErrors = Partial<Record<keyof ReviewForm, string>>;
const existingReviews = [{
  initials: 'TR',
  name: 'T. Robinson',
  location: 'Cottage City, MD',
  rating: 5,
  comment: 'Fast, friendly, and affordable! I got my tires replaced the same day. The team was professional and explained everything clearly. Highly recommend Ameritech!',
  bgStyle: {
    background: '#822433'
  }
}, {
  initials: 'MC',
  name: 'M. Carter',
  location: 'Suitland, MD',
  rating: 5,
  comment: "They explained everything clearly and helped me choose the right tires for my car. Great service at a fair price. I won't go anywhere else for my tires!",
  bgStyle: {
    background: '#000000'
  }
}, {
  initials: 'DJ',
  name: 'D. Johnson',
  location: 'Capitol Heights, MD',
  rating: 5,
  comment: 'Had a blowout at 2am and called Ameritech. They were there in under 40 minutes. Incredible service, incredibly professional. Saved my night.',
  bgStyle: {
    background: '#822433'
  }
}, {
  initials: 'KW',
  name: 'K. Williams',
  location: 'Cottage City, MD',
  rating: 5,
  comment: "Best tire shop in the DMV. Fair prices, honest advice, and they never try to upsell you on things you don't need. My whole family comes here.",
  bgStyle: {
    background: '#000000'
  }
}];
type ReviewEntry = (typeof existingReviews)[0];
export function ReviewsPage() {
  const [reviews, setReviews] = useState<ReviewEntry[]>(existingReviews);
  const [form, setForm] = useState<ReviewForm>({
    name: '',
    location: '',
    rating: 0,
    comment: ''
  });
  const [errors, setErrors] = useState<ReviewFormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const validate = (): ReviewFormErrors => {
    const errs: ReviewFormErrors = {};
    if (!form.name.trim()) errs.name = 'Please enter your name';
    if (!form.location) errs.location = 'Please select a location';
    if (form.rating === 0) errs.rating = 'Please select a star rating';
    if (!form.comment.trim()) errs.comment = 'Please write a review';else if (form.comment.trim().length < 20) errs.comment = 'Review must be at least 20 characters';
    return errs;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    // Build initials from name
    const initials = form.name.trim().split(' ').map(w => w[0]?.toUpperCase() ?? '').slice(0, 2).join('');
    const newReview: ReviewEntry = {
      initials: initials || '?',
      name: form.name.trim(),
      location: form.location,
      rating: form.rating,
      comment: form.comment.trim(),
      bgStyle: {
        background: '#822433'
      }
    };
    setTimeout(() => {
      setReviews(prev => [newReview, ...prev]);
      setSubmitted(true);
    }, 600);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof ReviewForm]) setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  };
  const inputClass = (hasError: boolean) => `w-full px-4 py-3 border rounded text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#822433] focus:border-transparent text-sm ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;
  return <div data-id="element-915">
      {/* Page Header */}
      <div className="relative py-24 px-4 sm:px-6 lg:px-8 text-center overflow-hidden bg-black" data-id="element-916">
        <img src="https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=1600&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" data-id="element-917" />
        <div className="relative z-10" data-id="element-918">
          <p className="font-bold text-sm uppercase tracking-widest mb-3" style={{
          color: '#CC0000'
        }} data-id="element-919">
            Customer Reviews
          </p>
          <h1 className="text-4xl sm:text-5xl font-black text-white" data-id="element-920">
            What Our Customers Say
          </h1>
          <div className="w-12 h-0.5 mx-auto mt-4 mb-4" style={{
          background: '#FECB00'
        }} data-id="element-921" />
          <p className="text-gray-400 text-base max-w-xl mx-auto" data-id="element-922">
            Real reviews from real DMV drivers. Trusted since 1995.
          </p>
        </div>
      </div>

      {/* Existing Reviews */}
      <section className="w-full bg-[#F5F5F5] py-20 px-4 sm:px-6 lg:px-8" data-id="element-923">
        <div className="max-w-6xl mx-auto" data-id="element-924">
          <h2 className="text-2xl sm:text-3xl font-black text-black text-center mb-12" data-id="element-925">
            Recent Reviews
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-926">
            {reviews.map((review, idx) => <div key={`${review.name}-${idx}`} className="bg-white rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] p-8 flex flex-col" data-id="element-927">
                <div className="flex gap-1 mb-5" aria-label={`${review.rating} out of 5 stars`} data-id="element-928">
                  {[...Array(5)].map((_, i) => <span key={i} className={`text-xl ${i < review.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-929">
                      ★
                    </span>)}
                </div>
                <blockquote className="text-black text-base leading-relaxed flex-1 mb-6 italic" data-id="element-930">
                  "{review.comment}"
                </blockquote>
                <div className="flex items-center gap-3" data-id="element-931">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0" style={review.bgStyle} data-id="element-932">
                    {review.initials}
                  </div>
                  <div data-id="element-933">
                    <div className="font-bold text-black text-sm" data-id="element-934">
                      {review.name}
                    </div>
                    <div className="text-xs" style={{
                  color: '#616365'
                }} data-id="element-935">
                      {review.location}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Leave a Review Form */}
      <section className="w-full bg-white py-20 px-4 sm:px-6 lg:px-8" data-id="element-936">
        <div className="max-w-2xl mx-auto" data-id="element-937">
          <div className="text-center mb-12" data-id="element-938">
            <p className="font-bold text-sm uppercase tracking-widest mb-3" style={{
            color: '#822433'
          }} data-id="element-939">
              Share Your Experience
            </p>
            <h2 className="text-2xl sm:text-3xl font-black text-black" data-id="element-940">
              Leave a Review
            </h2>
            <p className="text-base mt-3" style={{
            color: '#616365'
          }} data-id="element-941">
              Your feedback helps us improve and helps other customers find us.
            </p>
          </div>

          {submitted ? <div className="text-center py-16" data-id="element-942">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-943">
                <CheckCircleIcon size={32} className="text-green-500" data-id="element-944" />
              </div>
              <h3 className="text-2xl font-black text-black mb-3" data-id="element-945">
                Thank You!
              </h3>
              <p className="text-base max-w-sm mx-auto mb-8" style={{
            color: '#616365'
          }} data-id="element-946">
                Your review has been posted and is now visible above. We
                appreciate you sharing your experience!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-947">
                <button onClick={() => {
              setSubmitted(false);
              setForm({
                name: '',
                location: '',
                rating: 0,
                comment: ''
              });
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="inline-flex items-center justify-center font-bold text-sm px-8 py-3 rounded transition-colors border-2" style={{
              borderColor: '#822433',
              color: '#822433'
            }} onMouseEnter={e => {
              e.currentTarget.style.background = '#822433';
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#822433';
            }} data-id="element-948">
                  Write Another Review
                </button>
                <Link to="/" className="inline-flex items-center justify-center text-white font-bold text-sm px-8 py-3 rounded transition-colors" style={{
              background: '#822433'
            }} onMouseEnter={e => e.currentTarget.style.background = '#6B1D29'} onMouseLeave={e => e.currentTarget.style.background = '#822433'} data-id="element-949">
                  Back to Home
                </Link>
              </div>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-950">
              {/* Star Rating */}
              <div data-id="element-951">
                <label className="block text-sm font-bold text-black mb-2" data-id="element-952">
                  Your Rating{' '}
                  <span style={{
                color: '#822433'
              }} data-id="element-953">
                    *
                  </span>
                </label>
                <div className="flex gap-2" data-id="element-954">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => {
                setForm(prev => ({
                  ...prev,
                  rating: star
                }));
                setErrors(prev => ({
                  ...prev,
                  rating: undefined
                }));
              }} onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)} className="text-4xl transition-transform hover:scale-110 focus:outline-none" aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`} data-id="element-955">
                      <span className={star <= (hoveredStar || form.rating) ? 'text-yellow-400' : 'text-gray-200'} data-id="element-956">
                        ★
                      </span>
                    </button>)}
                </div>
                {errors.rating && <p className="mt-1 text-xs text-red-500" data-id="element-957">{errors.rating}</p>}
              </div>

              {/* Name */}
              <div data-id="element-958">
                <label htmlFor="review-name" className="block text-sm font-bold text-black mb-1.5" data-id="element-959">
                  Your Name{' '}
                  <span style={{
                color: '#822433'
              }} data-id="element-960">
                    *
                  </span>
                </label>
                <input id="review-name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="First name or initials" className={inputClass(!!errors.name)} data-id="element-961" />
                {errors.name && <p className="mt-1 text-xs text-red-500" data-id="element-962">{errors.name}</p>}
              </div>

              {/* Location */}
              <div data-id="element-963">
                <label htmlFor="review-location" className="block text-sm font-bold text-black mb-1.5" data-id="element-964">
                  Which Location Did You Visit?{' '}
                  <span style={{
                color: '#822433'
              }} data-id="element-965">
                    *
                  </span>
                </label>
                <select id="review-location" name="location" value={form.location} onChange={handleChange} className={`${inputClass(!!errors.location)} appearance-none bg-white ${!form.location ? 'text-gray-400' : ''}`} data-id="element-966">
                  <option value="" disabled data-id="element-967">
                    Select a location...
                  </option>
                  <option value="Cottage City, MD" data-id="element-968">Cottage City</option>
                  <option value="Capitol Heights, MD" data-id="element-969">Capitol Heights</option>
                  <option value="Suitland, MD" data-id="element-970">Suitland</option>
                </select>
                {errors.location && <p className="mt-1 text-xs text-red-500" data-id="element-971">{errors.location}</p>}
              </div>

              {/* Comment */}
              <div data-id="element-972">
                <label htmlFor="review-comment" className="block text-sm font-bold text-black mb-1.5" data-id="element-973">
                  Your Review{' '}
                  <span style={{
                color: '#822433'
              }} data-id="element-974">
                    *
                  </span>
                </label>
                <textarea id="review-comment" name="comment" value={form.comment} onChange={handleChange} placeholder="Tell us about your experience..." rows={5} className={`w-full px-4 py-3 border rounded text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#822433] focus:border-transparent text-sm resize-none ${errors.comment ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} data-id="element-975" />
                <div className="flex justify-between mt-1" data-id="element-976">
                  {errors.comment ? <p className="text-xs text-red-500" data-id="element-977">{errors.comment}</p> : <span data-id="element-978" />}
                  <span className="text-xs text-gray-400" data-id="element-979">
                    {form.comment.length} chars
                  </span>
                </div>
              </div>

              <button type="submit" className="w-full text-white font-black text-base py-4 rounded transition-colors duration-200" style={{
            background: '#822433'
          }} onMouseEnter={e => e.currentTarget.style.background = '#6B1D29'} onMouseLeave={e => e.currentTarget.style.background = '#822433'} data-id="element-980">
                Submit Review
              </button>
            </form>}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-[#000000] py-14 px-4 sm:px-6 lg:px-8 text-center" data-id="element-981">
        <h2 className="text-2xl font-black text-white mb-3" data-id="element-982">
          Ready to Experience Ameritech?
        </h2>
        <p className="text-gray-400 text-base mb-8 max-w-lg mx-auto" data-id="element-983">
          Join thousands of satisfied DMV drivers. Book your service today.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-984">
          <Link to="/contact" className="inline-flex items-center justify-center text-white font-bold text-base px-8 py-4 rounded transition-colors duration-200" style={{
          background: '#822433'
        }} onMouseEnter={e => e.currentTarget.style.background = '#6B1D29'} onMouseLeave={e => e.currentTarget.style.background = '#822433'} data-id="element-985">
            Book an Appointment
          </Link>
          <Link to="/locations" className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-black font-bold text-base px-8 py-4 rounded transition-colors duration-200" data-id="element-986">
            Find a Location
          </Link>
        </div>
      </section>
    </div>;
}